import React, { useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import {
    FaArrowDown, FaArrowUp, FaComment, FaHeart, FaArrowRight,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import SignalsService from "../../../services/Signals";


const GRADIENTS = ["from-orange-400 to-red-500", "from-blue-400 to-indigo-500", "from-green-400 to-emerald-500", "from-purple-400 to-pink-500", "from-yellow-400 to-orange-500", "from-cyan-400 to-blue-500",];

const SignalsCard = () => {
    const [activeTab, setActiveTab] = useState("all");
    const [showMore, setShowMore] = useState(false);
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const IMAGE_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

    useEffect(() => {
        fetchSignals();
    }, []);

    const targetOf = (v) => {
        const n = v !== null && typeof v === "object" ? v.target : v;
        return typeof n === "number" && isFinite(n) ? n : null;
    };
    const isDone = (v) => v !== null && typeof v === "object" && v.done === true;

    const calcPct = (type, entry, price) => {
        if (!entry || price == null) return null;
        const diff = type === "buy" ? price - entry : entry - price;
        return (diff / entry) * 100;
    };

    const fetchSignals = async () => {
        try {
            setLoading(true);
            const res = await SignalsService.getBestSignals({
                page: 1, limit: 40,
            });

            const mapped = (res?.data?.data?.docs || []).map((item, index) => {
                const isBuy = item.type === "buy";

                // entry: limit if set, otherwise trigger
                const limit = targetOf(item.limit);
                const entry = limit > 0 ? limit : (targetOf(item.trigger) ?? 0);

                // outcome: which TP was actually hit, or the stop loss
                const hitTps = (item.tp || []).filter((t) => t.done);
                const lastHit = hitTps[hitTps.length - 1];

                let outcome = "PENDING";
                let resultPrice = null;
                if (lastHit) {
                    outcome = "HIT";
                    resultPrice = lastHit.target;
                } else if (isDone(item.sl)) {
                    outcome = "STOPPED";
                    resultPrice = targetOf(item.sl);
                } else if (item.status === "completed") {
                    outcome = "EXPIRED";
                }

                const pct = calcPct(item.type, entry, resultPrice);
                const profit = pct == null ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;

                const days = Math.floor((Date.now() - new Date(item.createdAt).getTime()) / 86400000);
                const daysAgo = days === 0 ? "Today" : days === 1 ? "1 day ago" : `${days} days ago`;

                const state = pct == null ? "none" : pct > 0 ? "profit" : pct < 0 ? "loss" : "even";

                return {
                    id: item._id,
                    pair: item.symbol?.symbol?.slice(0, 4) || "??",
                    symbol: item.symbol?.name || item.symbol?.symbol || "Unknown",
                    author: item.owner?.fullName || item.owner?.userName || "Admin",
                    daysAgo,
                    action: isBuy ? "BUY" : "SELL",
                    actionColor: isBuy ? "green" : "red",
                    profit,
                    profitState: state,
                    outcome,
                    entryPrice: entry,
                    targetHit: resultPrice ?? "—",
                    status: outcome,
                    likes: item.likesCount ?? 0,
                    comments: item.commentsCount ?? 0,
                    createdAt: item.createdAt,
                    gradient: GRADIENTS[index % GRADIENTS.length],
                    img: item.image?.path ? IMAGE_BASE + item.image.path : null,
                };
            });

            setSignals(mapped);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Client-side filter for Last Week / All Time
    // const getFilteredSignals = () => {
    //   let list = signals;

    //   if (activeTab === "week") {
    //     const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    //     list = list.filter((s) => new Date(s.createdAt).getTime() >= oneWeekAgo);
    //   }

    //   // Always sort by newest to oldest (regardless of tab)
    //   // return [...list].sort(
    //   //   (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    //   // );
    //   //This was removed due the list being sorted createdAt and messing it up by not being sorted by likes uncomment it if you want the sort by date to come back -MM
    //   return list;
    // };

    const getFilteredSignals = () => {
        let list = signals;

        if (activeTab === "week") {
            const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

            list = list.filter((s) => {
                const date = new Date(s.createdAt).getTime();

                return date >= oneWeekAgo;
            });
        }


        return list;
    };

    const getActionStyles = (actionColor) => {
        if (actionColor === "green") {
            return {
                bgColor: "bg-green-100", textColor: "text-green-800", icon: <FaArrowUp className="pr-1 pt-0.5" />,
            };
        }
        return {
            bgColor: "bg-red-100", textColor: "text-red-800", icon: <FaArrowDown className="pr-1 pt-0.5" />,
        };
    };

    const formatNumber = (value) => {
        const number = Number(value);
        if (value === "—" || isNaN(number)) return "—";
        return number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const PROFIT_STYLES = {
        profit: { box: "bg-green-50", text: "text-green-600", label: "PROFIT" },
        loss: { box: "bg-red-50", text: "text-red-600", label: "LOSS" },
        even: { box: "bg-gray-50", text: "text-gray-500", label: "RETURN" },
        none: { box: "bg-gray-50", text: "text-gray-500", label: "" },
    };

    const renderSignalCard = (signal) => {
        const actionStyles = getActionStyles(signal.actionColor);
        const ps = PROFIT_STYLES[signal.profitState];


        return (<div
            key={signal.id}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300"
            onClick={() => navigate(`/Signal/${signal.id}`)}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                    {!signal.img ? (<div
                        className={`w-16 h-16 bg-gradient-to-br ${signal.gradient} rounded-2xl flex items-center justify-center`}
                    >
                        <span className="text-white font-bold text-lg">
                            {signal.pair}
                        </span>
                    </div>) : (<div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    >
                        <img src={signal.img} alt={signal.pair}
                            className="w-16 h-16  rounded-2xl flex items-center justify-center" />
                    </div>

                    )}
                    <div>
                        <h3 className="font-bold text-xl text-dark">{signal.symbol}</h3>
                        <p className="text-gray-500 text-sm">
                            by
                            <span className="block truncate w-24">
                                {signal.author}
                            </span>
                            • {signal.daysAgo}
                        </p>
                        <div className="flex items-center mt-2">
                            <span
                                className={`${actionStyles.bgColor} ${actionStyles.textColor} flex px-4 py-1 rounded-full text-sm font-bold`}
                            >
                                {actionStyles.icon}
                                {signal.action}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div className={`${ps.box} px-4 py-2 rounded-xl`}>
                        <p className={`text-2xl font-bold ${ps.text}`}>{signal.profit}</p>
                        <p className={`${ps.text} text-sm font-semibold`}>{ps.label}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-gray-500 text-xs mb-1">Entry Price</p>
                    <p className="font-bold text-dark">
                        {formatNumber(signal.entryPrice)}
                    </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-gray-500 text-xs mb-1">Target Hit</p>
                    <p className="font-bold text-green-600">{formatNumber(signal.targetHit)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-gray-500 text-xs mb-1">Status</p>
                    <p className="font-bold text-sm text-green-600">{signal.status}</p>
                </div>
            </div>

            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                    <span className="flex items-center gap-2 text-gray-500">
                        <FaHeart className="text-red-400" />
                        <span className="font-semibold pt-0.5">{signal.likes}</span>
                    </span>
                    <span className="flex items-center gap-2 text-gray-500">
                        <FaComment className="text-blue-400" />
                        <span className="font-semibold pt-0.5">{signal.comments}</span>
                    </span>
                </div>
                <button
                    onClick={() => navigate(`/signal/${signal.id}`)}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white w-10 h-10 rounded-xl font-semibold text-sm hover:shadow-lg transition-all flex items-center justify-center"
                >
                    <FaArrowRight />
                </button>
            </div>
        </div>);
    };

    const filteredSignals = getFilteredSignals();

    return (<section
        id="top-signals-section"
        className="px-6 py-8 bg-gradient-to-br from-white/50 to-purple-50/50"
    >
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
                <div
                    className="w-8 h-8 text-white bg-gradient-to-r from-green-400 to-blue-500 rounded-lg flex items-center justify-center">
                    <FaStar className="animate-pulse" />
                </div>
                <h2 className="text-xl font-bold text-dark">Best Signals</h2>
            </div>
            <button
                onClick={() => setShowMore((prev) => !prev)}
                className="text-primary font-semibold text-sm hover:text-secondary transition-colors"
            >
                {showMore ? "View Less" : "View All"}
            </button>
        </div>

        <div
            id="signals-filter-tabs"
            className="bg-white p-1 rounded-xl flex gap-2 items-center justify-around mb-8 shadow-sm"
        >
            <button
                onClick={() => setActiveTab("week")}
                className={`w-full text-center py-2 rounded-xl font-semibold transition-all duration-300 ${activeTab === "week" ? "bg-gradient-to-r from-[#7c90e9] to-[#8B9FEB] text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}
            >
                Last Week
            </button>
            <button
                onClick={() => setActiveTab("all")}
                className={`w-full text-center py-2 rounded-xl font-semibold transition-all duration-300 ${activeTab === "all" ? "bg-gradient-to-r from-[#7c90e9] to-[#8B9FEB] text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}
            >
                All Time
            </button>
        </div>

        {loading ? (<div className="text-center py-10 text-gray-500">Loading
            signals…</div>) : filteredSignals.length === 0 ? (
                <div className="text-center py-10 text-gray-500">No signals found</div>) : (<div
                    className={`space-y-5 transition-all duration-300 ${showMore ? "h-[650px] overflow-y-auto bg-white rounded-2xl border border-gray-100 p-1" : "h-full overflow-y-hidden"}`}
                >
                    {filteredSignals.map((signal) => renderSignalCard(signal))}
                </div>)}
    </section>
    );
};

export default SignalsCard;
